import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import StarIcon from "@mui/icons-material/Star";
import PersonIcon from "@mui/icons-material/Person";
import "./ReportPage.css";
import awsConfig from "../aws-exports";

const ReportPage = () => {
  const API_URL = awsConfig.baseUrl;
  const navigate = useNavigate();

  const [selectedMacAddress, setSelectedMacAddress] = useState("");
  const [macAddresses, setMacAddresses] = useState([]); // State to store MAC addresses
  const [reportData, setReportData] = useState(null);

  // Fetch MAC addresses from the user's profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const idToken = localStorage.getItem("id_token");

        if (!idToken) {
          throw new Error("User is not authenticated. ID token is missing.");
        }

        const response = await fetch(`${API_URL}/profile`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch profile");
        }

        const profileData = await response.json();

        // Extract MAC addresses from the profile
        const macs = [
          profileData.macAddress1,
          profileData.macAddress2,
          profileData.macAddress3,
          profileData.macAddress4,
        ].filter(Boolean); // Filter out empty/null values

        setMacAddresses(macs);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchProfile();
  }, [API_URL]);

  const handleFetchReport = async () => {
    try {
      const response = await fetch(
        `${API_URL}/sensor-data?macAddress=${encodeURIComponent(selectedMacAddress)}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch report");
      }

      const fetchedData = await response.json();
      setReportData(fetchedData);
    } catch (error) {
      console.error("Error fetching report:", error);
      setReportData({ error: "Failed to fetch report" });
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
  };

  const handleLogout = () => {
    console.log("Redirecting to logout page...");
    navigate("/logout"); // Navigate to the logout route
  };

  return (
    <div className="dashboard-container">
      <aside className="sidebar">
        <div className="sidebar-header">Chameye</div>
        <List>
          <ListItem
            button
            onClick={() => navigate("/dashboard")}
            style={{ cursor: "pointer" }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#1976d2")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "")}
          >
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard Page" />
          </ListItem>
          <ListItem
            button
            onClick={() => navigate("/profile")}
            style={{ cursor: "pointer" }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#1976d2")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "")}
          >
            <ListItemIcon>
              <LightbulbIcon />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </ListItem>
          <ListItem
            button
            onClick={() => navigate("/report")}
            style={{ cursor: "pointer" }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#1976d2")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "")}
          >
            <ListItemIcon>
              <StarIcon />
            </ListItemIcon>
            <ListItemText primary="Report" />
          </ListItem>
        </List>
        <Divider />
        <List>
          <ListItem
            button
            onClick={handleLogout}
            style={{ cursor: "pointer" }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#1976d2")
            }
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "")}
          >
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="Sign Out" />
          </ListItem>
        </List>
      </aside>
      <main className="content">
        <h2>Report Page</h2>
        <div className="input-container">
          <FormControl fullWidth variant="outlined">
            <InputLabel>Select MAC Address</InputLabel>
            <Select
              value={selectedMacAddress}
              onChange={(e) => setSelectedMacAddress(e.target.value)}
              label="Select MAC Address"
            >
              {macAddresses.map((mac, index) => (
                <MenuItem key={index} value={mac}>
                  {mac}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFetchReport}
            disabled={!selectedMacAddress}
          >
            Fetch
          </Button>
        </div>

        {reportData && !reportData.error && (
          <TableContainer component={Paper} className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Sensor Name</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Data 1</TableCell>
                  <TableCell>Data 2</TableCell>
                  <TableCell>Data 3</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{formatTimestamp(row.timestamp)}</TableCell>
                    <TableCell>{row.sensorName}</TableCell>
                    <TableCell>{row.event}</TableCell>
                    <TableCell>{row.data1}</TableCell>
                    <TableCell>{row.data2}</TableCell>
                    <TableCell>{row.data3}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {reportData && reportData.error && (
          <div className="error-message">{reportData.error}</div>
        )}
      </main>
    </div>
  );
};

export default ReportPage;